import React, { useState, useRef, useEffect } from 'react';
import { FaFileInvoiceDollar, FaFilePdf, FaRedo } from 'react-icons/fa';
import Swal from 'sweetalert2';
import YearSelector from '../../components/common/YearSelector';
import F22FormPage from '../../components/DeclaracionRenta/F22/F22FormPage';
import { hasModule } from '../../components/DeclaracionRenta/F22/registry';
import useYearSelector from '../../hooks/useYearSelector';
import declaracionRentaService from '../../services/declaracionRentaService';
import '../../styles/pages/ServiciosSII/DeclaracionRentaPage.css';

const CODIGOS_RESUMEN = [
    { cod: 'cod_1705', codigo: '1705', nombre: 'Base imponible afecta a IDPC del ejercicio' },
    { cod: 'cod_1545', codigo: '1545', nombre: 'CPTS positivo final' },
    { cod: 'cod_1704', codigo: '1704', nombre: 'Remesas, retiros o dividendos repartidos en el ejercicio, históricos' },
    { cod: 'cod_87',   codigo: '87',   nombre: 'Monto devolución solicitada' },
    { cod: 'cod_91',   codigo: '91',   nombre: 'Total a pagar (códigos 90 y 39)' },
];

const formatCLP = (value) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toLocaleString('es-CL');
};

const DeclaracionRentaPage = ({ token, id_company }) => {
    const {
        selectedYear,
        setSelectedYear,
        yearOptions
    } = useYearSelector(null, 5, 1);

    const [selectedRegimen, setSelectedRegimen] = useState('14d3');
    const [rentaGuardada, setRentaGuardada] = useState(null);
    const pdfInputRef = useRef(null);

    useEffect(() => {
        if (selectedYear === 2025 && selectedRegimen === '14d3') {
            declaracionRentaService.obtenerDatosGuardados(selectedYear, id_company, token)
                .then(result => {
                    if (result?.success && result.data) {
                        setRentaGuardada(result.data);
                    } else {
                        setRentaGuardada(null);
                    }
                })
                .catch(() => setRentaGuardada(null));
        } else {
            setRentaGuardada(null);
        }
    }, [selectedYear, selectedRegimen, id_company, token]);

    const handleCargarPdf = () => {
        pdfInputRef.current.click();
    };

    const handlePdfSelected = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        e.target.value = '';

        if (file.type !== 'application/pdf') {
            Swal.fire('Error', 'El archivo debe ser un PDF', 'error');
            return;
        }

        Swal.fire({
            title: 'Procesando declaración...',
            html: 'Claude está extrayendo los códigos del F-22...',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            const result = await declaracionRentaService.cargarDesdepdf(file, id_company, token);

            if (result.success) {
                setRentaGuardada(result.data);
                Swal.fire({
                    icon: 'success',
                    title: '¡Declaración cargada!',
                    html: `<p>${result.message}</p><p>Año tributario: <strong>${result.anio_tributario}</strong></p>`,
                    confirmButtonText: 'Ver resumen'
                });
            } else {
                Swal.fire('Error', result.message || 'No se pudo procesar el PDF', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message || 'Error al cargar la declaración de renta', 'error');
        }
    };

    const mostrarCargaPdf = selectedYear === 2025 && selectedRegimen === '14d3';

    return (
        <div className="declaracion-renta-page">
            <div className="page-header">
                <div className="header-content">
                    <div className="title-section">
                        <FaFileInvoiceDollar className="page-icon" />
                        <div>
                            <h1>Declaración de Renta AT {selectedYear}</h1>
                            <p className="page-subtitle">
                                Formulario 22 - Declaración anual de impuesto a la renta
                            </p>
                        </div>
                    </div>
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                        label="Año Tributario"
                        className="header-year-selector"
                    />
                </div>
            </div>

            <div className="declarar-renta-container">
                <div className="regimen-selector">
                    <label className="regimen-label">Régimen Tributario:</label>
                    <div className="regimen-options">
                        <button
                            className={`regimen-btn ${selectedRegimen === '14d3' ? 'active' : ''}`}
                            onClick={() => setSelectedRegimen('14d3')}
                        >
                            Pro PYME (14D3)
                        </button>
                        <button
                            className={`regimen-btn ${selectedRegimen === '14d8' ? 'active' : ''}`}
                            onClick={() => setSelectedRegimen('14d8')}
                        >
                            14D8
                        </button>
                    </div>
                </div>

                {hasModule(selectedYear, selectedRegimen) ? (
                    <F22FormPage
                        key={`${selectedYear}-${selectedRegimen}`}
                        anioTributario={selectedYear}
                        regimen={selectedRegimen}
                    />
                ) : mostrarCargaPdf ? (
                    <div className="carga-pdf-container">
                        {rentaGuardada ? (
                            <>
                                <div className="renta-resumen-header">
                                    <h3 className="renta-resumen-titulo">
                                        Resumen AT {rentaGuardada.anio_tributario}
                                    </h3>
                                    <button
                                        className="btn-recargar"
                                        onClick={handleCargarPdf}
                                        title="Cargar otro PDF"
                                    >
                                        <FaRedo className="btn-icon" />
                                        Cargar nuevamente
                                    </button>
                                </div>
                                <div className="renta-tabla-container">
                                    <table className="renta-tabla">
                                        <thead>
                                            <tr>
                                                <th>Código</th>
                                                <th>Descripción</th>
                                                <th className="col-valor">Valor (CLP)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {CODIGOS_RESUMEN.map(({ cod, codigo, nombre }) => (
                                                <tr key={cod}>
                                                    <td className="col-codigo">{codigo}</td>
                                                    <td>{nombre}</td>
                                                    <td className="col-valor">
                                                        {formatCLP(rentaGuardada[cod])}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <>
                                <FaFilePdf className="carga-pdf-icon" />
                                <p className="carga-pdf-texto">
                                    Carga el PDF del F-22 del SII para extraer y guardar automáticamente todos los códigos.
                                </p>
                                <button
                                    className="btn-cargar-declaracion"
                                    onClick={handleCargarPdf}
                                >
                                    <FaFilePdf className="btn-icon" />
                                    Cargar Declaración de Renta
                                </button>
                            </>
                        )}
                        <input
                            type="file"
                            ref={pdfInputRef}
                            accept="application/pdf"
                            onChange={handlePdfSelected}
                            style={{ display: 'none' }}
                        />
                    </div>
                ) : (
                    <div className="f22-loading">
                        <p>Función en desarrollo</p>
                    </div>
                )}
            </div>

            <div className="page-footer">
                <p className="footer-info">
                    <strong>Nota:</strong> Las declaraciones de renta deben ser presentadas ante el SII
                    durante el mes de abril de cada año. Asegúrese de contar con toda la documentación
                    respaldatoria antes de enviar.
                </p>
            </div>
        </div>
    );
};

export default DeclaracionRentaPage;
